/* cc-header — search docking + burger menu.
   When the hero search scrolls out of view, body gets .cc-docked:
   the docked search fills the header's navigation area, the inline
   menu hides and the burger appears. Burger toggles a frosted panel
   with staggered links. No hero search on the page → never docks. */

(() => {
  // iOS Safari AUTO-zooms into focused inputs, and its zoom heuristic
  // reads stale geometry when the search bar pins/reflows on focus —
  // one tap blew the page up 4x (Tony 2026-07-12). maximum-scale=1
  // disables only the AUTO zoom: user pinch-zoom still works on iOS
  // (Apple ignores maximum-scale for user gestures since iOS 10).
  if (/iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
    const vp = document.querySelector('meta[name="viewport"]');
    if (vp && !/maximum-scale/.test(vp.content)) vp.content += ', maximum-scale=1';
  }

  // live scroll direction (1 down / -1 up) — the dock/hide handoff needs it
  let scrollDir = 0;
  let _dirY = window.scrollY;
  window.addEventListener(
    'scroll',
    () => {
      const y = window.scrollY;
      scrollDir = y > _dirY ? 1 : y < _dirY ? -1 : scrollDir;
      _dirY = y;
    },
    { passive: true }
  );
  let setStackFn = () => {};
  let snapT;

  const heroSearch = document.querySelector('.cc-search--hero');
  const burger = document.querySelector('.cc-header-burger');
  const panel = document.querySelector('.cc-burger-panel');

  // Burger sits at the far LEFT of the bar (Tony 2026-07-05): make it
  // a direct grid child of .header with grid-area left-icon (CSS) —
  // auto-placement would push it onto its own row otherwise
  const headerEl = document.querySelector('.header');
  if (burger && headerEl) headerEl.prepend(burger);

  // Trophy chip joins the icons cluster on the right
  const icons = document.querySelector('.header__icons');
  const trophy = document.querySelector('.cc-trophy--header');
  if (trophy && icons) icons.prepend(trophy);

  // The frosted header's backdrop-filter makes it the containing block
  // for fixed descendants — the panel must live on <body> so
  // position:fixed is truly viewport-relative
  if (panel) document.body.appendChild(panel);

  // Nav overflow -> burger (Tony 2026-07-13: wrapped two-line nav at
  // mid widths with NO way to open the menu). The inline menu is only
  // allowed ONE line: the moment it would wrap, body.cc-nav-collapsed
  // hides it and the burger takes over (cc-header.css). Measured live
  // -- resize, font load, dock changes -- not a hardcoded breakpoint,
  // so it stays correct if menu items are added later.
  // NOTE the list is cc-mega-nav's ul (the original '.list-menu--inline'
  // selector was Dawn's stock class — it never existed in our mega-nav
  // markup, so the wrap guard was a silent no-op from 07-13 to 07-15).
  // Match by element so a future class rename can't kill it again.
  const inlineNavList = document.querySelector('.header__inline-menu ul');
  const measureNav = () => {
    if (!inlineNavList) return;
    const body = document.body;
    const had = body.classList.contains('cc-nav-collapsed');
    body.classList.remove('cc-nav-collapsed'); // measure the expanded state
    let wrapped = had; // unmeasurable (hidden while docked) -> keep verdict
    if (inlineNavList.offsetParent && inlineNavList.children.length > 1) {
      const items = inlineNavList.children;
      const first = items[0].getBoundingClientRect();
      const last = items[items.length - 1].getBoundingClientRect();
      wrapped = last.top - first.top > first.height * 0.5; // a second line exists
    }
    body.classList.toggle('cc-nav-collapsed', wrapped);
    // widened back to a one-line header: the burger just vanished --
    // never strand an open drawer with no X (Tony 2026-07-13)
    const burgerShows =
      wrapped || body.classList.contains('cc-docked') || window.matchMedia('(max-width: 989px)').matches;
    if (!burgerShows) {
      const drawer = document.querySelector('cc-menu-drawer');
      if (drawer && !drawer.hidden && typeof drawer.close === 'function') drawer.close();
    }
  };
  measureNav();
  window.addEventListener('resize', measureNav);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => measureNav());

  // Mobile dock overlay: its absolute containing block is .header
  // (Dawn keeps it position:sticky for a Safari z-index fix), which is
  // SHORTER than the wrapper — CSS top:100% overlapped the bar by ~8px
  // of stacked 90%-white (the "thick white line"). Measure the true
  // wrapper-bottom offset instead.
  const placeDock = () => {
    const dock = document.querySelector('.cc-header-dock');
    const wrapper = document.querySelector('.header-wrapper');
    const headerEl = document.querySelector('.header');
    if (!dock || !wrapper || !headerEl) return;
    if (window.matchMedia('(max-width: 989px)').matches) {
      dock.style.top = wrapper.offsetHeight - headerEl.offsetTop - 1 + 'px';
      // one shared frost pane paints behind bar+trophy (cc-header.css)
      document.body.style.setProperty('--cc-dock-h', dock.offsetHeight + 'px');
    } else {
      dock.style.top = '';
      document.body.style.removeProperty('--cc-dock-h');
    }
    // Permanently-docked pages: the mobile dock is an absolute OVERLAY
    // (kept out of flow so the sticky header never changes height) —
    // compensate with static top padding on the page content, set once
    // at load so scroll anchoring never sees a shift.
    const main = document.getElementById('MainContent');
    if (main && document.body.classList.contains('cc-docked-static')) {
      main.style.paddingTop = window.matchMedia('(max-width: 989px)').matches
        ? dock.offsetHeight + 'px'
        : '';
    }
  };
  window.addEventListener('resize', placeDock);

  // No hero search on this page (product/collection/etc.) → the docked
  // bar IS the header from load, so search stays in the bar the whole
  // time (Tony 2026-07-06: "we lose our search bar" on inner pages).
  if (!heroSearch) {
    document.body.classList.add('cc-docked', 'cc-docked-static');
    placeDock();
  }

  if (heroSearch) {
    new IntersectionObserver(
      ([entry]) => {
        // FREEZE while any search is open: the sheet forces scroll->0 to
        // pin the bar, which brings the hero "into view" and this
        // observer was UNDOCKING THE BAR MID-TYPING (Tony's recurring
        // missing-bar bug — homepage only, inner pages have no hero)
        if (
          document.body.classList.contains('cc-search-lock') ||
          document.body.classList.contains('cc-search-page-dim')
        )
          return;
        document.body.classList.toggle('cc-docked', !entry.isIntersecting);
        measureNav(); // undock re-shows the inline menu -- re-verdict wrap
        if (!entry.isIntersecting) {
          placeDock();
          if (scrollDir > 0 && window.scrollY > 200) {
            // arriving by scrolling DOWN: hide the whole stack in the
            // same frame AND WITHOUT TRANSITION — the 0.3s slide let
            // the fresh dock flash as a white box (Tony round 27)
            document.body.classList.add('cc-hdr-snap');
            setStackFn();
            document.body.classList.add('cc-hdr-hidden');
            clearTimeout(snapT);
            snapT = setTimeout(() => document.body.classList.remove('cc-hdr-snap'), 150);
          } else if (document.body.classList.contains('cc-hdr-hidden')) {
            setStackFn(); // dock just appeared — hide distance was stale (half-visible bar)
          }
        }
        if (entry.isIntersecting) closeBurger();
      },
      { rootMargin: '-80px 0px 0px 0px' }
    ).observe(heroSearch);
  }

  function closeBurger() {
    if (!panel || panel.hidden) return;
    document.body.classList.remove('cc-burger-open');
    burger.setAttribute('aria-expanded', 'false');
    setTimeout(() => (panel.hidden = true), 250); // after exit transition
  }

  function openBurger() {
    const header = document.querySelector('.header-wrapper');
    if (header) {
      panel.style.setProperty('--cc-panel-top', Math.round(header.getBoundingClientRect().bottom) + 8 + 'px');
    }
    panel.hidden = false;
    requestAnimationFrame(() => {
      document.body.classList.add('cc-burger-open');
      burger.setAttribute('aria-expanded', 'true');
    });
  }

  const mobile = window.matchMedia('(max-width: 989px)');

  if (burger) {
    burger.addEventListener('click', () => {
      // ONE menu at every width (Tony 2026-07-12: the desktop dropdown
      // vs full-page drawer inconsistency): the burger always toggles
      // the drawer — morphs to X, menu bleeds down from the top.
      // cc-burger-panel is dormant.
      const drawer = document.querySelector('cc-menu-drawer');
      if (drawer) drawer.hidden ? drawer.open(burger) : drawer.close();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeBurger();
    });

    document.addEventListener('click', (e) => {
      if (!panel.contains(e.target) && !burger.contains(e.target)) closeBurger();
    });

    // Shop accordion inside the panel (mega menu content while docked)
    panel.addEventListener('click', (e) => {
      const toggle = e.target.closest('.cc-burger-panel__toggle');
      if (!toggle) return;
      toggle.setAttribute('aria-expanded', toggle.getAttribute('aria-expanded') !== 'true');
    });
  }

  // Hide-on-scroll (Tony 2026-07-11): the WHOLE header stack — bar plus
  // the docked search/trophy overlay rows — slides away scrolling down
  // and returns on scroll-up or when scrolling stops. The hide distance
  // is measured (--cc-hdr-stack): the dock rows are absolute overlays
  // OUTSIDE the section's flow height, so translateY(-100%) alone left
  // them peeking (Tony's first-pass feedback).
  const sectionHeader = document.querySelector('.section-header.shopify-section-group-header-group');
  if (sectionHeader) {
    const setStack = () => {
      const dock = document.querySelector('.cc-header-dock');
      let h = sectionHeader.offsetHeight;
      if (dock && window.matchMedia('(max-width: 989px)').matches && document.body.classList.contains('cc-docked')) {
        h += dock.offsetHeight;
      }
      document.body.style.setProperty('--cc-hdr-stack', h + 24 + 'px'); /* +24 clears the shadow */
    };
    setStackFn = setStack;
    // Round 25 (Tony: "still bouncing!"): pure direction model, the way
    // apps do it. Scroll down -> hidden immediately. Scroll up -> back.
    // At the very top -> always shown. NO return-on-stop timer (every
    // pause between flicks resurfaced the header and the next flick
    // hid it again — that WAS the bounce), and no 200px force-show band
    // (crossing it mid-scroll flip-flopped the state).
    let lastY = window.scrollY;
    window.addEventListener(
      'scroll',
      () => {
        const y = window.scrollY;
        const dy = y - lastY;
        lastY = y;
        const busy =
          document.body.classList.contains('cc-burger-open') ||
          document.body.classList.contains('cc-search-page-dim');
        if (y < 40 || busy) {
          document.body.classList.remove('cc-hdr-hidden');
          return;
        }
        /* y-gate: hiding while the page hasn't scrolled past the
           header's own footprint just exposes a white band (Tony,
           rewards page) — gate ADDING hidden only; never force-show */
        if (dy > 4 && y > 180) {
          setStack();
          if (!document.body.classList.contains('cc-hdr-hidden')) syncCartFloat();
          document.body.classList.add('cc-hdr-hidden');
        } else if (dy < -8) {
          document.body.classList.remove('cc-hdr-hidden');
        }
      },
      { passive: true }
    );
  }

  // Floating cart while the header is hidden (Tony 2026-07-11): white
  // circle top-right mirroring the live bubble; clicking it drives the
  // REAL header icon (programmatic .click() fires on transformed-away
  // elements — DAWN_NOTES section 13), so the drawer opens as normal.
  let syncCartFloat = () => {};
  if (sectionHeader && document.getElementById('cart-icon-bubble')) {
    const float = document.createElement('a');
    float.className = 'cc-cart-float';
    float.href = (window.routes && window.routes.cart_url) || '/cart';
    float.setAttribute('aria-label', 'Cart');
    document.body.appendChild(float);
    syncCartFloat = () => {
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble) float.innerHTML = bubble.innerHTML;
    };
    syncCartFloat();
    // bubble HTML is server-swapped on every cart change; cartUpdate fires after
    // PUB_SUB_EVENTS is a top-level const (no window property) — bare identifier (2026-10-01)
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => setTimeout(syncCartFloat, 150));
    }
    float.addEventListener('click', (e) => {
      const real = document.getElementById('cart-icon-bubble');
      if (real) {
        e.preventDefault();
        real.click();
      }
    });
  }

  // Optimistic cart badge (Tony 2026-08-06): the bubble is server-swapped
  // after the ATC fetch + section render, which reads as lag. Bump the
  // count the moment an add-to-cart form submits (capture phase — fires
  // for the main ATC, the sticky bar's programmatic submit, and quick-add
  // forms alike); the server-rendered swap reconciles moments later and
  // is authoritative, including after a failed add.
  document.addEventListener(
    'submit',
    (e) => {
      const form = e.target;
      if (!form || !form.action || form.action.indexOf('/cart/add') === -1) return;
      const bubbleHost = document.getElementById('cart-icon-bubble');
      if (!bubbleHost) return;
      const qtyEl = form.querySelector('input[name="quantity"]');
      const qty = Math.max(1, parseInt(qtyEl && qtyEl.value, 10) || 1);
      let bubble = bubbleHost.querySelector('.cart-count-bubble');
      if (!bubble) {
        // empty cart renders no bubble — create one so the count shows
        // instantly; the next section render replaces it wholesale
        bubble = document.createElement('div');
        bubble.className = 'cart-count-bubble';
        bubble.innerHTML = '<span aria-hidden="true">0</span>';
        bubbleHost.appendChild(bubble);
      }
      const num = bubble.querySelector('span[aria-hidden]');
      if (num) num.textContent = String((parseInt(num.textContent, 10) || 0) + qty);
      syncCartFloat();
    },
    true
  );

  // Floating search pill (Tony 2026-07-12): sits left of the cart
  // circle while the header is hidden; tap = full search experience.
  if (sectionHeader && document.querySelector('cc-search')) {
    const spill = document.createElement('button');
    spill.type = 'button';
    spill.className = 'cc-search-float';
    spill.setAttribute('aria-label', 'Search');
    spill.innerHTML =
      '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><circle cx="9" cy="9" r="6.5" stroke="currentColor" stroke-width="1.8"/><path d="m14 14 4 4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg><span>Search</span>';
    document.body.appendChild(spill);
    spill.addEventListener('click', () => {
      document.body.classList.remove('cc-hdr-hidden');
      const target =
        (document.body.classList.contains('cc-docked') && document.querySelector('.cc-search--header')) ||
        document.querySelector('.cc-search--hero') ||
        document.querySelector('cc-search');
      const inp = target && target.querySelector('.cc-search__input');
      if (inp) {
        inp.focus();
        inp.click(); // engage() handles the already-focused case
      }
    });
  }
})();
